<script setup>
import {computed, ref} from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import { cn } from '@/lib/utils'
import {
  Home,
  Search,
  Calendar,
  CalendarDays,
  MessageSquare,
  Bookmark,
  BookMarked,
  Settings,
  Bell,
  GraduationCap,
  BookOpen,
  Users,
  HelpCircle,
  ChevronDown,
  Gauge,
  User,
  LogOut,
  CreditCard,
  List,
  DollarSign,
  X,
  UserRoundCog,
  ShieldCheck
} from 'lucide-vue-next'
import { Avatar, AvatarFallback, AvatarImage } from '@/Components/ui/avatar'
import { Button } from '@/Components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Components/ui/dropdown-menu'

import {
  logout,
} from '@/routes/index'
import teacher from '@/routes/teacher'
import HamburgerIcon from "./icons/HamburgerIcon.vue";
import {ToastClose} from "radix-vue";
import SupportChatModal from '@/Components/SupportChatModal.vue';
import ImpersonateBanner from '@/Components/ImpersonateBanner.vue'
import StripeConnectBanner from '@/Components/StripeConnectBanner.vue'
import ImpersonateModal from '@/Components/Modals/ImpersonateModal.vue'

const page = usePage()
const user = computed(() => page.props.auth?.user)
const todaysSessionCount = computed(() => page.props.todaysSessionCount ?? 0)
const unreadMessageCount = computed(() => page.props.unreadMessageCount ?? 0)

const isMobileOpen = ref(false);
const showSupportModal = ref(false);
const showImpersonateModal = ref(false);
const impersonateEnabled = computed(() => page.props.impersonate?.enabled)

// Determine if user is a teacher or student
const isTeacher = computed(() => user.value?.user_type === 'teacher')
const isStudent = computed(() => ['student', 'customer'].includes(user.value?.user_type))
const isAdmin = computed(() => ['system', 'limited-system'].includes(user.value?.user_type))

// Dashboard URL based on user type
const dashboardUrl = computed(() => isTeacher.value ? '/teacher/dashboard' : '/student/dashboard')

// Navigation items based on user type
const studentNavigation = computed(() => [
  { name: 'Dashboard', href: '/student/dashboard', icon: Home },
  { name: 'Explore', href: '/student/explore', icon: Search },
  { name: 'My Resources', href: '/student/resources', icon: BookMarked },
  { name: 'Schedule', href: '/student/schedule', icon: Calendar },
  { name: 'Messages', href: '/student/messages', icon: MessageSquare, badge: unreadMessageCount.value > 0 ? unreadMessageCount.value : undefined },
  { name: 'Subscriptions', href: '/student/subscriptions', icon: CreditCard },
  { name: 'Settings', href: '/student/settings/profile', icon: Settings },
])

const teacherNavigation = computed(() => [
  { name: 'Dashboard', href: '/teacher/dashboard', icon: Home },
  { name: 'My Content', href: teacher.content.index.url(), icon: BookOpen },
  { name: 'My Students', href: teacher.students.index.url(), icon: Users },
  { name: 'Messages', href: '/teacher/messages', icon: MessageSquare, badge: unreadMessageCount.value > 0 ? unreadMessageCount.value : undefined },
  { name: 'Earnings', href: '/teacher/earnings', icon: DollarSign },
  { name: 'Calendar', href: '/teacher/schedules', icon: CalendarDays },
  { name: 'Settings', href: teacher.settings.index.url(), icon: Settings },
])

const navigation = computed(() => isTeacher.value ? teacherNavigation.value : studentNavigation.value)

const isUrl = (path) => {
  if (!path || typeof path !== 'string' || path === '#') return false
  return page.url.startsWith(path)
}

// User display info
const userFirstName = computed(() => user.value?.first_name || user.value?.name?.split(' ')[0] || 'User')
const userFullName = computed(() => user.value?.name || 'User')
const userInitials = computed(() => {
  const name = user.value?.name || 'U'
  return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().substring(0, 2)
})
</script>

<style>
    @media (max-width: 48rem) {
        .dashboard-offcanvas{
            @apply translate-x-[-100%] transition-transform duration-300;
            &.open{
                @apply translate-x-[0];
            }

        }
    }
</style>

<template>
  <div class="flex flex-col h-screen overflow-hidden">
    <!-- Impersonate Banner -->
    <ImpersonateBanner />
    <StripeConnectBanner />

    <div class="flex flex-1 overflow-hidden">
    <!-- Sidebar -->
    <aside :class="{open: isMobileOpen}" class="dashboard-offcanvas z-10 fixed md:relative max-sm:top-0 max-sm:bottom-0 w-80 md:w-64 bg-white border-r border-gray-200 flex flex-col">
      <!-- Logo Header - Links to main index -->
      <div class="px-6 py-4 border-b border-gray-200">
        <Link :href="dashboardUrl" class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
            <GraduationCap class="text-white w-5 h-5" />
          </div>
          <div class="flex-grow-1">
            <h1 class="text-xl font-bold text-gray-900">Bindle</h1>
            <p class="text-xs text-gray-500">Learning Platform</p>
          </div>
          <X class="md:hidden text-gray-500 cursor-pointer" @click.prevent="isMobileOpen = false" />
        </Link>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 p-4 space-y-1 overflow-y-auto">
        <Link
          v-for="item in navigation"
          :key="item.name"
          :href="item.href"
          :class="cn(
            'flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors',
            isUrl(item.href)
              ? 'bg-blue-600 text-white'
              : 'text-gray-700 hover:bg-gray-50'
          )"
        >
          <component :is="item.icon" class="w-5 h-5" />
          <span class="font-medium">{{ item.name }}</span>
          <span
            v-if="item.badge"
            class="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold"
          >
            {{ item.badge }}
          </span>
        </Link>
      </nav>

      <!-- Bottom Section - Help only -->
      <div class="p-4 border-t border-gray-200">
        <button
          @click="showSupportModal = true"
          class="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors w-full"
        >
          <HelpCircle class="w-5 h-5" />
          <span class="font-medium">Help</span>
        </button>
      </div>
    </aside>

    <!-- Main Content Area -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <!-- Top Header -->
      <header class="bg-white border-b border-gray-200 p-4 md:px-8">
        <div class="flex flex-row items-center justify-between">
          <div class="md:hidden">
            <HamburgerIcon  :is-open="isMobileOpen" @click="isMobileOpen = true" />
          </div>
          <!-- Welcome Message -->
          <div>
            <div class="flex items-center gap-3 flex-wrap">
              <h1 class="text-lg md:text-2xl font-bold text-gray-900">
                  <span class="md:hidden">Hi</span><span class="hidden md:inline">Welcome back</span>, {{ userFirstName }}!
              </h1>
            </div>
            <p class="hidden md:block text-gray-500 text-sm mt-1">
              Here's what's happening with your learning today
            </p>
          </div>

          <!-- Right Actions -->
          <div class="flex items-center space-x-4">
            <!-- Search -->
            <div class="relative hidden">
              <input
                type="text"
                placeholder="Search content..."
                class="w-80 pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>

            <!-- Notifications -->
            <button class="relative p-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <Bell class="w-5 h-5" />
              <span
                v-if="todaysSessionCount > 0"
                class="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-semibold"
              >
                {{ todaysSessionCount }}
              </span>
            </button>

            <!-- User Dropdown -->
            <DropdownMenu>
              <DropdownMenuTrigger as-child>
                <Button variant="ghost" class="flex items-center space-x-2 hover:bg-transparent p-0">
                  <Avatar class="w-10 h-10 border-2 border-gray-200">
                    <AvatarImage
                      :src="user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(userFullName)}&background=3B82F6&color=fff`"
                      :alt="userFullName"
                    />
                    <AvatarFallback>{{ userInitials }}</AvatarFallback>
                  </Avatar>
                  <ChevronDown class="text-gray-400 w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" class="w-56">
                <!-- Dashboard -->
                <DropdownMenuItem as-child>
                  <Link :href="dashboardUrl" class="cursor-pointer w-full flex items-center">
                    <Gauge class="mr-2 h-4 w-4" />
                    <span>Dashboard</span>
                  </Link>
                </DropdownMenuItem>

                <!-- Teacher Menu Items -->
                <template v-if="isTeacher">
                  <!-- Profile -->
                  <DropdownMenuItem as-child>
                    <Link :href="teacher.settings.personalInfo.url()" class="cursor-pointer w-full flex items-center">
                      <User class="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </Link>
                  </DropdownMenuItem>

                  <!-- Settings -->
                  <DropdownMenuItem as-child>
                    <Link :href="teacher.settings.subjects.url()" class="cursor-pointer w-full flex items-center">
                      <Settings class="mr-2 h-4 w-4" />
                      <span>Settings</span>
                    </Link>
                  </DropdownMenuItem>
                </template>

                <!-- Student Menu Items -->
                <template v-if="isStudent">
                  <!-- Profile -->
                  <DropdownMenuItem as-child>
                    <Link href="/student/settings/profile" class="cursor-pointer w-full flex items-center">
                      <User class="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </Link>
                  </DropdownMenuItem>

                  <!-- Subscriptions -->
                  <DropdownMenuItem as-child>
                    <Link href="/student/subscriptions" class="cursor-pointer w-full flex items-center">
                      <CreditCard class="mr-2 h-4 w-4" />
                      <span>Subscriptions</span>
                    </Link>
                  </DropdownMenuItem>

                  <!-- Settings -->
                  <DropdownMenuItem as-child>
                    <Link href="/student/settings/notifications" class="cursor-pointer w-full flex items-center">
                      <Settings class="mr-2 h-4 w-4" />
                      <span>Settings</span>
                    </Link>
                  </DropdownMenuItem>
                </template>

                <!-- Admin Panel (system users only) -->
                <template v-if="isAdmin">
                  <DropdownMenuSeparator />
                  <DropdownMenuItem as-child>
                    <a href="/admin" class="cursor-pointer w-full flex items-center text-purple-600 focus:text-purple-600">
                      <ShieldCheck class="mr-2 h-4 w-4" />
                      <span>Admin Panel</span>
                    </a>
                  </DropdownMenuItem>
                </template>

                <!-- Impersonate (dev only) -->
                <template v-if="impersonateEnabled">
                  <DropdownMenuSeparator />
                  <DropdownMenuItem class="cursor-pointer w-full flex items-center text-amber-600 focus:text-amber-600" @click="showImpersonateModal = true">
                    <UserRoundCog class="mr-2 h-4 w-4" />
                    <span>Impersonate</span>
                  </DropdownMenuItem>
                </template>

                <DropdownMenuSeparator />

                <!-- Help & Support -->
                <DropdownMenuItem class="cursor-pointer w-full flex items-center" @click="showSupportModal = true">
                  <HelpCircle class="mr-2 h-4 w-4" />
                  <span>Help & Support</span>
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <!-- Sign Out -->
                <DropdownMenuItem as-child>
                  <Link :href="logout()" method="post" as="button" class="w-full cursor-pointer flex items-center text-red-600 focus:text-red-600">
                    <LogOut class="mr-2 h-4 w-4" />
                    <span>Sign out</span>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <!-- Main Content -->
      <main class="flex-1 bg-gray-50" :class="$slots.flush ? 'overflow-hidden' : 'overflow-y-auto'">
        <template v-if="$slots.flush">
          <slot name="flush" />
        </template>
        <div v-else class="p-4 md:p-8 max-w-7xl md:mx-auto">
          <slot />
        </div>
      </main>
    </div>

    <!-- Support Chat Modal -->
    <SupportChatModal :open="showSupportModal" @close="showSupportModal = false" />

    <!-- Impersonate Modal -->
    <ImpersonateModal :show="showImpersonateModal" @close="showImpersonateModal = false" />
    </div>
  </div>
</template>
